import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Award, ShieldCheck, Sparkles, Trophy, GraduationCap, Building2,
  Zap, X, RefreshCw, Search, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const AWARD_META = {
  verified:           { label: "Verified",        icon: ShieldCheck, color: "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30" },
  certified:          { label: "Certified",       icon: Award,       color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  top:                { label: "Top",             icon: Trophy,      color: "bg-violet-500/10 text-violet-400 border-violet-500/30" },
  top_placement:      { label: "Top Placement",   icon: Building2,   color: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
  student_excellence: { label: "Student Excellence", icon: GraduationCap, color: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
  founder:            { label: "Founder Award",   icon: Sparkles,    color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
  innovation:         { label: "Innovation",      icon: Sparkles,    color: "bg-pink-500/10 text-pink-400 border-pink-500/30" },
  emerging:           { label: "Emerging",        icon: Sparkles,    color: "bg-teal-500/10 text-teal-400 border-teal-500/30" },
  excellence:         { label: "Excellence",      icon: Sparkles,    color: "bg-orange-500/10 text-orange-400 border-orange-500/30" },
};
const STATUS_COLORS = {
  pending_review: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  active:         "bg-ec-emerald/20 text-ec-emerald border-ec-emerald/40",
  suspended:      "bg-orange-500/20 text-orange-300 border-orange-500/40",
  revoked:        "bg-ec-error/20 text-ec-error border-ec-error/40",
  expired:        "bg-zinc-700 text-zinc-400 border-zinc-600",
};

export default function AdminAwards() {
  const [tab, setTab] = useState("suggestions"); // suggestions | pending_review | active | revoked | expired | analytics
  const [suggestions, setSuggestions] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);

  const loadSuggestions = useCallback(async () => {
    try { setSuggestions((await api.get("/admin/awards/suggestions")).data.suggestions); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  const loadList = useCallback(async (status) => {
    try {
      const { data } = await api.get("/admin/awards", { params: { status, q } });
      setRows(data.rows); setCounts(data.counts);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [q]);

  useEffect(() => {
    if (tab === "suggestions" || tab === "analytics") loadSuggestions();
    if (tab !== "suggestions" && tab !== "analytics") loadList(tab);
  }, [tab, loadSuggestions, loadList]);

  const runEngine = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/awards/run-engine");
      toast.success(`Engine ran · ${data.auto_issued} auto-issued · ${data.pending_review} pending review`);
      loadSuggestions(); if (tab !== "suggestions") loadList(tab);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <AdminLayout
      subtitle="Module 28"
      title="Institution Awards Center"
      actions={
        <Button onClick={runEngine} disabled={running} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="awards-run-engine">
          <Zap className="w-4 h-4" /> {running ? "Running…" : "Run Auto-Award Engine"}
        </Button>
      }
    >
      <Tabs tab={tab} setTab={setTab} counts={{ ...counts, suggestions: suggestions.length }} />

      {tab === "suggestions" && <SuggestionsTab list={suggestions} onActioned={loadSuggestions} />}

      {tab !== "suggestions" && tab !== "analytics" && (
        <>
          <div className="mb-4">
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search institution…" className="rounded-sm h-9 pl-9 bg-white/5 border-white/10 text-white" data-testid="awards-search" />
            </div>
          </div>
          <AwardsList rows={rows} onPick={setSelected} />
        </>
      )}

      {tab === "analytics" && <Analytics counts={counts} suggestions={suggestions} />}

      {selected && <AwardDrawer award={selected} onClose={() => setSelected(null)} onActioned={() => { setSelected(null); loadList(tab); }} />}
    </AdminLayout>
  );
}

function Tabs({ tab, setTab, counts }) {
  const tabs = [
    { k: "suggestions",    label: "AI Suggestions" },
    { k: "pending_review", label: "Pending Review" },
    { k: "active",         label: "Approved" },
    { k: "revoked",        label: "Revoked" },
    { k: "expired",        label: "Expired" },
    { k: "analytics",      label: "Analytics" },
  ];
  return (
    <div className="flex flex-wrap gap-px bg-white/10 border border-white/10 mb-5" data-testid="awards-tabs">
      {tabs.map((t) => (
        <button key={t.k} onClick={() => setTab(t.k)}
          className={`flex-1 min-w-[140px] px-3 py-3 text-left ${tab === t.k ? "bg-ec-emerald/10 border-b-2 border-ec-emerald" : "bg-ec-ink hover:bg-white/5"}`}
          data-testid={`awards-tab-${t.k}`}>
          <div className="eyebrow text-zinc-400 text-[10px]">{t.label}</div>
          <div className={`font-display text-xl font-black mt-1 ${tab === t.k ? "text-ec-emerald" : "text-white"}`}>{counts[t.k] ?? 0}</div>
        </button>
      ))}
    </div>
  );
}

function SuggestionsTab({ list, onActioned }) {
  const [busyId, setBusyId] = useState(null);
  const approve = async (s) => {
    setBusyId(s.institution_id + s.award_type);
    try {
      await api.post("/admin/awards/manual", { institution_id: s.institution_id, award_type: s.award_type, note: `Approved from AI suggestion. Reasons: ${(s.reasons||[]).join("; ")}` });
      toast.success(`${s.institution_name} → ${s.award_label} issued`);
      onActioned();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  if (!list.length) {
    return (
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-10 text-center text-zinc-500" data-testid="awards-suggestions-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
          No award suggestions right now. Click "Run Auto-Award Engine" above to recalculate eligibility.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="awards-suggestions-list">
      {list.map((s) => {
        const meta = AWARD_META[s.award_type];
        const Icon = meta?.icon || Award;
        const busy = busyId === s.institution_id + s.award_type;
        return (
          <Card key={s.institution_id + s.award_type} className="rounded-sm border-white/10 bg-white/[0.02]">
            <CardContent className="p-4 flex flex-wrap gap-4 items-start" data-testid={`suggestion-${s.institution_id}-${s.award_type}`}>
              <div className={`w-12 h-12 rounded-sm border flex items-center justify-center ${meta?.color || ""}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center flex-wrap gap-2">
                  <h4 className="font-display text-lg font-bold text-white">{s.institution_name || "—"}</h4>
                  <Badge className={`rounded-sm border ${meta?.color}`}>{s.award_label}</Badge>
                  <Badge className="rounded-sm bg-white/10 border-white/10 text-zinc-300">Trust Score: {s.trust_score}/100</Badge>
                  {s.auto_issuable && <Badge className="rounded-sm bg-ec-emerald/20 text-ec-emerald border-ec-emerald/40">Auto-issuable</Badge>}
                </div>
                <ul className="mt-2 text-xs text-zinc-400 grid sm:grid-cols-2 gap-1">
                  {(s.reasons || []).map((r, i) => <li key={`${s.institution_id}-${s.award_type}-reason-${i}`} className="flex gap-1.5"><CheckCircle2 className="w-3 h-3 text-ec-emerald mt-0.5" />{r}</li>)}
                </ul>
                <div className="text-[10px] font-mono-plex text-zinc-500 mt-2">AI Recommendation: <span className="text-ec-emerald">{s.ai_recommendation}</span></div>
              </div>
              <Button onClick={() => approve(s)} disabled={busy} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid={`suggestion-approve-${s.institution_id}-${s.award_type}`}>
                <CheckCircle2 className="w-4 h-4" /> {busy ? "Issuing…" : "Issue Award"}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function AwardsList({ rows, onPick }) {
  if (rows.length === 0) return <Card className="rounded-sm border-white/10 bg-white/[0.02]"><CardContent className="p-10 text-center text-zinc-500" data-testid="awards-list-empty">No awards in this tab.</CardContent></Card>;
  return (
    <Card className="rounded-sm border-white/10 bg-white/[0.02]">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="awards-list-table">
          <thead className="bg-white/5">
            <tr className="text-left">
              <th className="px-4 py-3 eyebrow text-zinc-500">Award #</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Institution</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Type</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Issued</th>
              <th className="px-4 py-3 eyebrow text-zinc-500">Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const meta = AWARD_META[a.award_type];
              return (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`award-row-${a.id}`}>
                  <td className="px-4 py-3 font-mono-plex text-xs text-zinc-300">{a.award_no}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-white">{a.institution?.organization_name || "—"}</div><div className="text-[10px] font-mono-plex text-zinc-500">{a.institution?.ethic_id || ""}</div></td>
                  <td className="px-4 py-3"><Badge className={`rounded-sm border ${meta?.color}`}>{meta?.label}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`rounded-sm border ${STATUS_COLORS[a.status]}`}>{a.status?.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-400">{(a.issued_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-400">{(a.expires_at || "—").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => onPick(a)} className="rounded-sm h-7 border-white/10 text-white hover:bg-white/10" data-testid={`award-open-${a.id}`}>Manage</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function AwardDrawer({ award, onClose, onActioned }) {
  const [note, setNote] = useState("");
  const meta = AWARD_META[award.award_type];
  const act = async (decision) => {
    try {
      await api.post(`/admin/awards/${award.id}/action`, { decision, note });
      toast.success(`Award ${decision}`);
      onActioned();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-end" onClick={onClose} data-testid="award-drawer">
      <div className="bg-ec-ink border-l border-white/10 w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-ec-ink border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div>
            <Badge className={`rounded-sm border ${meta?.color}`}>{meta?.label}</Badge>
            <h3 className="font-display text-lg font-bold text-white mt-1">{award.institution?.organization_name}</h3>
            <div className="text-[10px] font-mono-plex text-zinc-500">{award.award_no}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="award-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <KV label="Status" value={<Badge className={`rounded-sm border ${STATUS_COLORS[award.status]}`}>{award.status.replace("_", " ")}</Badge>} />
          <KV label="Issued" value={<span className="font-mono-plex text-xs">{(award.issued_at || "").slice(0, 19).replace("T", " ") || "—"}</span>} />
          <KV label="Expires" value={<span className="font-mono-plex text-xs">{(award.expires_at || "—").slice(0, 19).replace("T", " ")}</span>} />
          <KV label="Trust Score @ award" value={award.trust_score_at_award || "—"} />
          <KV label="Signature" value={<span className="font-mono-plex text-[10px] text-zinc-500">{award.signature}</span>} />
          {award.reasons?.length > 0 && (
            <section><div className="eyebrow text-zinc-500 mb-1">Reasons</div><ul className="text-xs text-zinc-300 list-disc list-inside space-y-0.5">{award.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul></section>
          )}
          <section className="border-t border-white/10 pt-4">
            <div className="eyebrow text-zinc-500 mb-2">Action note (optional)</div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-sm bg-white/5 border-white/10 text-white text-sm" data-testid="award-action-note" />
            <div className="grid grid-cols-2 gap-2 mt-3">
              {award.status === "pending_review" && (<>
                <Button onClick={() => act("approve")} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="award-action-approve">Approve</Button>
                <Button variant="outline" onClick={() => act("reject")} className="rounded-sm border-ec-error/30 text-ec-error hover:bg-ec-error/10" data-testid="award-action-reject">Reject</Button>
              </>)}
              {award.status === "active" && (<>
                <Button variant="outline" onClick={() => act("renew")} className="rounded-sm border-white/10 text-white hover:bg-white/10" data-testid="award-action-renew">Renew</Button>
                <Button variant="outline" onClick={() => act("suspend")} className="rounded-sm border-orange-500/30 text-orange-300 hover:bg-orange-500/10" data-testid="award-action-suspend">Suspend</Button>
                <Button variant="outline" onClick={() => act("audit")} className="rounded-sm border-white/10 text-white hover:bg-white/10" data-testid="award-action-audit">Request Audit</Button>
                <Button variant="outline" onClick={() => act("revoke")} className="rounded-sm border-ec-error/30 text-ec-error hover:bg-ec-error/10" data-testid="award-action-revoke">Revoke</Button>
              </>)}
              {award.status === "suspended" && (
                <Button onClick={() => act("renew")} className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink" data-testid="award-action-renew-suspended">Reactivate</Button>
              )}
              <Button variant="outline" onClick={() => window.open(`/verify/institution/${award.award_no}`, "_blank")} className="rounded-sm border-white/10 text-white hover:bg-white/10" data-testid="award-public-verify">Open public verify ↗</Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Analytics({ counts, suggestions }) {
  const cards = [
    { k: "All", v: counts.all || 0, color: "bg-white/10 text-white" },
    { k: "Active", v: counts.active || 0, color: "bg-ec-emerald/10 text-ec-emerald" },
    { k: "Pending", v: counts.pending_review || 0, color: "bg-amber-500/10 text-amber-400" },
    { k: "Suspended", v: counts.suspended || 0, color: "bg-orange-500/10 text-orange-400" },
    { k: "Revoked", v: counts.revoked || 0, color: "bg-ec-error/10 text-ec-error" },
    { k: "Expired", v: counts.expired || 0, color: "bg-zinc-700 text-zinc-400" },
  ];
  return (
    <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="awards-analytics">
      {cards.map((c) => (
        <Card key={c.k} className={`rounded-sm border-white/10 ${c.color}`}>
          <CardContent className="p-5">
            <div className="eyebrow text-current opacity-70">{c.k}</div>
            <div className="font-display text-3xl font-black mt-1">{c.v}</div>
          </CardContent>
        </Card>
      ))}
      <Card className="rounded-sm border-white/10 bg-white/[0.02] col-span-full mt-3">
        <CardContent className="p-5">
          <div className="eyebrow text-zinc-500 mb-3">Top suggestions waiting for review</div>
          <ul className="space-y-2">
            {suggestions.slice(0, 5).map((s) => (
              <li key={`${s.institution_id}-${s.award_type}`} className="flex items-center gap-2 text-sm text-white">
                <Badge className={`rounded-sm border ${AWARD_META[s.award_type]?.color}`}>{AWARD_META[s.award_type]?.label}</Badge>
                <span className="flex-1">{s.institution_name}</span>
                <span className="text-xs text-zinc-500 font-mono-plex">Trust {s.trust_score}/100</span>
              </li>
            ))}
            {suggestions.length === 0 && <li className="text-zinc-500 text-xs italic">None right now.</li>}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-white/5">
      <div className="w-32 eyebrow text-zinc-500 text-[10px]">{label}</div>
      <div className="flex-1 text-zinc-200 break-words">{value}</div>
    </div>
  );
}
